import { option } from 'fp-ts';
import { constFalse, pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';

import { getStringProp } from '@mews/fp';

import { ApplicationState } from '../../../types';
import { getCurrencyCode } from '../../app/selectors';
import { getSelectedHotelConfigurationId } from '../../configuration/selectors';
import {
    getEndDate,
    getEndTimeUtc,
    getSelectedHotelId,
    getStartDate,
    getStartTimeUtc,
    getUniqueOccupanciesOccupancyData,
} from '../../resourceOrder/selectors';
import { getEnterpriseServiceId } from '../services/selectors';
import { getProductPricingHash } from './getProductPricingHash';
import { getProductPricingMetaValue, getProductPricingProducts } from './selectors';

const getCurrentProductPricingHash = (state: ApplicationState) => {
    const enterpriseId = getSelectedHotelId(state);
    const configurationId = getSelectedHotelConfigurationId(state, { enterpriseId });
    const startDate = getStartDate(state);
    const endDate = getEndDate(state);
    const serviceId = getEnterpriseServiceId(state, { enterpriseId });
    const occupancyData = getUniqueOccupanciesOccupancyData(state);
    const currencyCode = getCurrencyCode(state);
    const startTimeUtc = getStartTimeUtc(state);
    const endTimeUtc = getEndTimeUtc(state);

    if (startDate == null || endDate == null || configurationId == null || currencyCode == null || serviceId == null) {
        return option.none;
    }

    return pipe(
        getProductPricingHash({
            occupancyData,
            endDate: endTimeUtc ?? endDate,
            startDate: startTimeUtc ?? startDate,
            configurationId,
            currencyCode,
        }),
        option.fromNullable
    );
};

const getCurrentProductPricing = (state: ApplicationState) =>
    pipe(
        getCurrentProductPricingHash(state),
        option.flatMap((productPricingHash) => getProductPricingProducts(state, { productPricingHash }))
    );

const getCurrentProductPricingMeta = (state: ApplicationState) =>
    pipe(
        getCurrentProductPricingHash(state),
        option.flatMap((productPricingHash) => getProductPricingMetaValue(state, { productPricingHash }))
    );

export const hasCurrentProductPricingFailed = (state: ApplicationState) =>
    pipe(
        getCurrentProductPricingMeta(state),
        option.map(({ hasFailed }) => hasFailed),
        option.getOrElse(constFalse)
    );

export const isCurrentProductPricingFetching = (state: ApplicationState) =>
    pipe(
        getCurrentProductPricingMeta(state),
        option.map(({ isFetching }) => isFetching),
        option.getOrElse(constFalse)
    );

export const getCurrentProductPrice = createSelector(
    getCurrentProductPricing,
    getStringProp('productId'),
    (productPricingOption, productId) =>
        pipe(
            productPricingOption,
            option.chainNullableK((productPricing) => productPricing?.[productId])
        )
);
